import { useEffect, useRef, useState } from 'react';
import { api, CallMessage } from '../api';
import { LoginBackdrop } from '../components/LoginBackdrop';
import { Logo } from '../components/Logo';
import { IconFile } from '../components/icons';
import { MissingNote } from '../components/MissingNote';
import { CallProvider, GuestEntry, useCall } from '../chat/call/CallProvider';
import { CallLayer } from '../chat/call/CallLayer';
import { greeting } from '../lib/greeting';

type Door = Awaited<ReturnType<typeof api.roomDoor>>;
type RoomFile = { id: string; name: string; size: number };

const fmtSize = (n: number): string =>
  n < 1024 ? `${n} Б` : n < 1024 * 1024 ? `${Math.round(n / 1024)} КБ` : `${(n / 1024 / 1024).toFixed(1)} МБ`;

/*
 * Вход в комнату по ссылке. Оболочка та же, что у страницы входа: человек
 * снаружи приложения, ему нужен один экран с одним действием.
 *
 * Дверь опрашивается раз в двадцать секунд: комната может открыться, пока
 * человек ждёт, и обновлять страницу руками он не должен.
 */
export function GuestRoom({ token }: { token: string }) {
  const [door, setDoor] = useState<Door | null>(null);
  // ссылку присылают в мессенджере, где её легко обрезать: по битому коду
  // человек должен увидеть причину, а не пустую карточку
  const [broken, setBroken] = useState(false);
  const doorRef = useRef<Door | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [entered, setEntered] = useState<GuestEntry | null>(null);
  // звонок только что закончился: пока не пришёл свежий опрос двери, дверь
  // ещё думает, что комната открыта, и без этого признака гость увидел бы
  // прежнюю форму входа вместо объяснения, что разговор окончен
  const [justEnded, setJustEnded] = useState(false);
  /** Идёт опрос итогов: лист мог не успеть собраться к моменту выхода. */
  const [loadingFiles, setLoadingFiles] = useState(false);
  /** Файлы были, но семь дней вышли и их убрали (решение Ильи 15.09.2026). */
  const [expired, setExpired] = useState(false);
  // Итоги доски (раздел 7.3 спеки): файлы, накопленные к выходу гостя из звонка.
  const [files, setFiles] = useState<RoomFile[]>([]);
  /** Переписка звонка: гость читает её здесь, пока жива его кука (7 суток). */
  /** Переписка нужна только ради вложений: сами сообщения гостю не показываем. */
  const [messages, setMessages] = useState<CallMessage[]>([]);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .roomDoor(token)
        .then((d) => {
          if (!alive) return;
          doorRef.current = d;
          setDoor(d);
          setBroken(false);
        })
        .catch(() => {
          if (alive) setBroken((prev) => prev || !doorRef.current);
        });
    void load();
    const t = setInterval(load, 20_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [token]);

  /*
   * Файлы и вложения переписки на странице комнаты (раздел 7.3): не только
   * сразу после выхода из звонка, но и при обычном возврате — сервер отдаёт
   * то, что накопилось на куку гостя.
   *
   * Спрашиваем несколько раз с растущей паузой. Событие о конце звонка
   * уходит участникам первым, раньше сборки файла и записи итогов (так
   * задумано: экран должен закрыться до обрыва потока), поэтому первый же
   * запрос легко приходит в пустоту — лист ещё собирается в PDF. Баг Ильи
   * 15.09.2026: сохранил лист, сразу завершил звонок, и у гостя файлов нет.
   *
   * Прежде вложения переписки не грузились вовсе: их запрос стоял после
   * возврата из эффекта и не выполнялся никогда.
   */
  useEffect(() => {
    if (entered) return;
    let alive = true;
    let timer = 0;
    setLoadingFiles(true);
    const DELAYS = [0, 1200, 2500, 5000];
    const ask = (step: number) => {
      void Promise.all([
        api
          .roomFiles(token)
          .then((r) => {
            setExpired(r.expired);
            return r.files;
          })
          .catch(() => [] as RoomFile[]),
        api.roomMessages(token).catch(() => [] as CallMessage[]),
      ]).then(([list, msgs]) => {
        if (!alive) return;
        setFiles(list);
        setMessages(msgs);
        const empty = !list.length && !msgs.some((m) => m.files.length);
        const next = step + 1;
        // Пусто и попытки ещё остались — ждём: файл мог не успеть собраться.
        if (empty && next < DELAYS.length) {
          timer = window.setTimeout(() => ask(next), DELAYS[next]);
          return;
        }
        setLoadingFiles(false);
      });
    };
    ask(0);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [token, entered, justEnded]);

  if (entered) {
    return (
      <CallProvider guest={entered}>
        <CallLayer />
        <BackWhenOver
          onOver={() => {
            setEntered(null);
            setJustEnded(true);
          }}
        />
      </CallProvider>
    );
  }

  const enter = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await api.roomJoin(token, name);
      // пропуск в комнату медиасервера приходит тем же ответом: второго
      // запроса за ним нет, гостю нечем его получить
      setEntered({
        token,
        participantId: res.participantId,
        url: res.url,
        mediaToken: res.token,
        call: res.call,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось войти');
    } finally {
      setBusy(false);
    }
  };

  // Вложения переписки: отдельного списка на сервере нет, файл всегда живёт
  // при своём сообщении.
  const chatFiles = messages.flatMap((m) => m.files);

  const opensAt = door?.opensAt
    ? new Date(door.opensAt * 1000).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : '';

  /*
   * Экран после звонка (решение Ильи 15.09.2026). Гостю он показывается
   * вместо формы входа и не сменяется сам: прежде опрос комнаты каждые
   * двадцать секунд сбрасывал признак окончания, и благодарность превращалась
   * обратно в приглашение войти.
   *
   * Содержимое — только то, что осталось на руках: файлы доски и вложения
   * переписки. Самой переписки здесь нет, она про разговор, а не про итог.
   */
  if (justEnded && !entered) {
    const boardRows = files.map((f) => ({ key: `b${f.id}`, name: f.name, size: f.size, href: api.roomFileUrl(token, f.id) }));
    const chatRows = chatFiles.map((f) => ({ key: `c${f.id}`, name: f.name, size: f.size, href: api.roomMessageFileUrl(token, f.id) }));
    const nothing = !boardRows.length && !chatRows.length;
    const list = (title: string, rows: typeof boardRows) =>
      rows.length > 0 && (
        <div className="field">
          <span className="field-label">{title}</span>
          <div className="row-list">
            {rows.map((f) => (
              <a key={f.key} className="row static" href={f.href} download={f.name}>
                <span className="ec-ico">
                  <IconFile />
                </span>
                <span className="row-text">
                  <span className="row-title">{f.name}</span>
                  <span className="row-sub">{fmtSize(f.size)}</span>
                </span>
              </a>
            ))}
          </div>
        </div>
      );

    return (
      <div className="login-wrap">
        <LoginBackdrop />
        <div className="login-head">
          <Logo variant="full" className="login-logo" />
          <div className="login-kind">Корпоративная система</div>
        </div>
        <div className="login-card">
          <h1 className="login-hi">Спасибо, что были с нами на звонке</h1>
          <p className="login-invite">
            {loadingFiles
              ? 'Разговор закончен. Собираем файлы…'
              : nothing
                ? expired
                  ? 'Разговор закончен. Файлы больше недоступны: срок хранения семь дней.'
                  : 'Разговор закончен. Файлов после него не осталось.'
                : 'Разговор закончен. Вот что осталось после него — файлы доступны семь дней.'}
          </p>
          {list('Файлы доски', boardRows)}
          {list('Файлы из переписки', chatRows)}
          {door?.state === 'open' && (
            <button className="btn outline wide" onClick={() => setJustEnded(false)}>
              Вернуться в комнату
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <LoginBackdrop />
      <div className="login-head">
        <Logo variant="full" className="login-logo" />
        <div className="login-kind">Корпоративная система</div>
      </div>
      <div className="login-card">
        {/* Гостя встречаем по-человечески: приветствие по времени суток, кто и
            куда зовёт, и просьба представиться (решение Ильи 15.09.2026).
            Экран после разговора — отдельный, он выше. */}
        <h1 className="login-hi">{`${greeting(new Date().getHours())}!`}</h1>
        <p className="login-invite">
          {door?.host ? `${door.host} приглашает вас на звонок` : 'Вас приглашают на звонок'}
          {door?.title ? ` «${door.title}»` : ''}. Представьтесь, пожалуйста.
        </p>

        {door?.state === 'open' && (
          <form className="form" onSubmit={enter}>
            {/* сотрудника и вернувшегося гостя сервер узнал по куке — имя уже есть */}
            {!door.me && (
              <div className="field">
                <label htmlFor="guest-name">
                  Как вас зовут <i>*</i>
                </label>
                <input
                  id="guest-name"
                  className={`input${name.trim().length < 2 ? ' missing' : ''}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
                {name.trim().length < 2 && <MissingNote />}
              </div>
            )}
            <button className="btn primary wide" disabled={busy || (!door.me && name.trim().length < 2)}>
              {busy ? 'Входим…' : 'Войти в звонок'}
            </button>
          </form>
        )}
        {door?.state === 'soon' && (
          <div className="sub">{opensAt ? `Комната откроется в ${opensAt}` : 'Комната ещё не открыта'}</div>
        )}
        {door?.state === 'closed' && <div className="sub">Звонок завершён</div>}
        {broken && <div className="sub">Ссылка недействительна: комнаты нет или её уже закрыли</div>}
        {error && <div className="banner crit">{error}</div>}

        {/* Переписку гостю не показываем: итог про файлы, а не про разговор
            (решение Ильи 15.09.2026). Вложения из неё идут отдельной
            категорией — сами сообщения остаются в звонке. */}
        {chatFiles.length > 0 && (
          <div className="field">
            <span className="field-label">Файлы из переписки</span>
            <div className="row-list">
              {chatFiles.map((f) => (
                <a
                  key={f.id}
                  className="row static"
                  href={api.roomMessageFileUrl(token, f.id)}
                  download={f.name}
                >
                  <span className="ec-ico">
                    <IconFile />
                  </span>
                  <span className="row-text">
                    <span className="row-title">{f.name}</span>
                    <span className="row-sub">{fmtSize(f.size)}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Итоги доски (раздел 7.3 спеки): файлы, накопленные к выходу гостя из звонка. Нечего показывать — блока нет вовсе. */}
        {files.length > 0 && (
          <div className="field">
            <span className="field-label">Файлы доски</span>
            <div className="row-list">
              {files.map((f) => (
                <a key={f.id} className="row static" href={api.roomFileUrl(token, f.id)} download={f.name}>
                  <span className="ec-ico">
                    <IconFile />
                  </span>
                  <span className="row-text">
                    <span className="row-title">{f.name}</span>
                    <span className="row-sub">{fmtSize(f.size)}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/*
 * Разговор кончился — гостя возвращаем на карточку комнаты. Приложения за
 * экраном звонка у него нет: без этого он остался бы перед пустой страницей.
 *
 * Ждём именно перехода из разговора в покой. В первый миг после входа звонок
 * ещё в покое: провайдер поднимает его своим эффектом, а он выполняется после
 * эффектов детей. Без этой памяти карточка возвращалась сразу и обрывала
 * вход — поток событий и комната закрывались, не успев открыться.
 */
function BackWhenOver({ onOver }: { onOver: () => void }) {
  const call = useCall();
  const started = useRef(false);
  useEffect(() => {
    if (call.phase !== 'idle') {
      started.current = true;
      return;
    }
    if (started.current) onOver();
  }, [call.phase, onOver]);
  return null;
}
